import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

// Motion variants for path drawing animation
const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i) => {
    const delay = i * 0.25;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { delay, type: "spring", duration: 1.2, bounce: 0 },
        opacity: { delay, duration: 0.01 },
      },
    };
  },
};

// PotredPathDrawing component with motion
const PotredPathDrawing = ({ className = "w-20 h-20 text-orange-500" }) => {
  const [animationKey, setAnimationKey] = useState(0);

  return (
    <motion.svg
      key={`loading-${animationKey}`}
      className={className}
      viewBox="0 0 24 24"
      initial="hidden"
      animate="visible"
      stroke="currentColor"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* pot knob */}
      <motion.path 
        d="m8.86 6.78-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8" 
        variants={draw} 
        custom={0} 
      />
      {/* pot lid */}
      <motion.path 
        d="m4 8 16-4" 
        variants={draw} 
        custom={1} 
      />
      {/* pot body (including sides/base) */}
      <motion.path 
        d="M2 12h20" 
        variants={draw} 
        custom={2} 
      />
      <motion.path 
        d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" 
        variants={draw} 
        custom={2} 
      />
    </motion.svg>
  );
};

// Main PageLoadingProgress component with controlled progress
const PageLoadingProgressMotion = ({
  isLoading,
  progress = 0,        // NEW: Controlled progress (0-100)
  onComplete,
  customMessage = "Loading..."
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setDisplayProgress(0);
      setIsComplete(false);
      return;
    }

    // Update display progress with smooth transition (CSS handles animation)
    setDisplayProgress(progress);

    // Trigger completion callback when reaching 100%
    if (progress >= 100 && !isComplete) {
      setIsComplete(true);
      setTimeout(() => {
        onComplete?.();
      }, 300);
    }
  }, [isLoading, progress, isComplete, onComplete]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-white dark:bg-slate-950 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-center max-w-md mx-auto px-6">
            {/* Potred Logo - PathDrawing Animation */}
            <motion.div
              className="mb-8 text-orange-500 mx-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <PotredPathDrawing className="w-20 h-20 mx-auto" />
            </motion.div>

            {/* Progress Bar */}
            <div className="w-full max-w-xs mx-auto mb-6">
              <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                  initial={{ width: "0%" }}
                  animate={{ width: `${displayProgress}%` }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Progress Percentage */}
            <motion.div
              className="text-lg font-bold text-orange-600 dark:text-orange-400 mb-2"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {Math.round(displayProgress)}%
            </motion.div>

            {/* Loading Message - Removed */}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLoadingProgressMotion;